import React from 'react';
import PropTypes from 'prop-types';
import RadioGroup from '@material-ui/core/RadioGroup';
import MUIRadio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormControl from '@material-ui/core/FormControl';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';

import { Tooltip } from 'core/components/tooltip';
import Divider from 'core/components/divider';
import styles from './radio.styles';

const Radio = ({
  id,
  name,
  classes,
  value,
  style,
  mode,
  onChange,
  onBlur,
  options,
  error,
  showError,
  helperText,
  defaultValue,
  disabled,
  className,
  label,
  tooltipLabel,
  optionsSideBySide,
  labelClassName,
  radioClassName,
  showDividers,
  containerClassName,
  ...props
}) => {
  return (
    <FormControl disabled={disabled} id={id} className={classNames(classes.formControl, className)} {...props}>
      <RadioGroup
        id={id}
        name={name}
        className={classNames(
          classes.container,
          {
            [classes.sideBySideContainer]: optionsSideBySide
          },
          containerClassName
        )}
        value={value || defaultValue}
        style={style}
        onChange={onChange}
        onBlur={onBlur}
      >
        {options.map((option) => (
          <React.Fragment key={option.value}>
            {showDividers && <Divider />}
            <FormControlLabel
              value={option.value}
              disabled={option.disabled}
              control={
                <MUIRadio
                  checked={value === option.value}
                  classes={{
                    root: classNames(radioClassName, {
                      [classes[`${mode}-radioRoot`]]: mode,
                      [classes.error]: error
                    }),
                    disabled: mode ? classes[`${mode}-radioRootDisabled`] : null
                  }}
                />
              }
              label={
                <>
                  {label || option.label}
                  {option.tooltipText && tooltipLabel && (
                    <Tooltip text={option.tooltipText} placement="bottom" label={tooltipLabel} />
                  )}
                </>
              }
              classes={{
                root: classNames({ [classes[`${mode}-radioRoot`]]: mode }),
                label: classNames(labelClassName, {
                  [classes.error]: error,
                  [classes[`${mode}-radioLabel`]]: !error && mode
                }),
                disabled: mode ? classes[`${mode}-radioLabelDisabled`] : null
              }}
            />
          </React.Fragment>
        ))}
      </RadioGroup>
      {error && showError && (
        <FormHelperText classes={{ root: classes.helperText }} id={id} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

Radio.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      tooltipText: PropTypes.string,
      disabled: PropTypes.bool
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  style: PropTypes.object,
  mode: PropTypes.oneOf([null, 'light', 'dark']),
  error: PropTypes.bool,
  helperText: PropTypes.any,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  label: PropTypes.node,
  tooltipLabel: PropTypes.string,
  optionsSideBySide: PropTypes.bool,
  labelClassName: PropTypes.string,
  radioClassName: PropTypes.string,
  showDividers: PropTypes.bool
};

Radio.defaultProps = {
  value: undefined,
  style: {},
  mode: null,
  error: false,
  helperText: undefined,
  defaultValue: undefined,
  disabled: false,
  className: '',
  label: '',
  tooltipLabel: null,
  optionsSideBySide: true,
  labelClassName: null,
  radioClassName: null,
  onBlur: undefined,
  showDividers: false,
  showError: true
};

export default withStyles(styles)(Radio);
